import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const HomePage = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [recentListings, setRecentListings] = useState([]);
  const [featuredListings, setFeaturedListings] = useState([]);
  const [categories, setCategories] = useState([]);
  const [meatProducts, setMeatProducts] = useState([]);
  const [breeders, setBreeders] = useState([]);
  const [auctions, setAuctions] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const itemsPerPage = 4;

  const API_URL = process.env.REACT_APP_API_URL;
  const BASE_URL = process.env.REACT_APP_BASE_URL || 'http://localhost:4000';

  const getImageUrl = (url) => {
    if (!url) return '/assets/placeholder.png';
    if (url.startsWith('http')) return url;
    return `${BASE_URL}${url}`;
  };

  const iconMap = {
    'Goat': 'pets', 'Sheep': 'grass', 'Bakra': 'psychology',
    'Camel': 'local_shipping', 'Other': 'star',
  };

  useEffect(() => {
    fetch(`${API_URL}/api/public/browse`)
      .then(r => r.json())
      .then(animals => {
        setRecentListings(animals);
        setFeaturedListings(animals.slice(0, 3));
      }).catch(() => {});

    fetch(`${API_URL}/api/public/breed-stats-public`)
      .then(r => r.json())
      .then(data => setCategories(data.map(row => ({
        name: row.category,
        icon: iconMap[row.category] || 'pets',
        listings: row.count,
      })))).catch(() => {});

    fetch(`${API_URL}/api/public/browse-meat`)
      .then(r => r.json())
      .then(data => setMeatProducts(data.slice(0, 4)))
      .catch(() => {});

    fetch(`${API_URL}/api/public/breeders`)
      .then(r => r.json())
      .then(data => setBreeders(data.slice(0, 4)))
      .catch(() => {});

    fetch(`${API_URL}/api/public/auctions?status=upcoming`)
      .then(r => r.json())
      .then(data => setAuctions(data.slice(0, 3)))
      .catch(() => {});
  }, [API_URL]);

  const features = [
    { icon: 'verified_user', title: 'Verified Breeders', description: 'Every seller undergoes a rigorous vetting process to ensure health and lineage accuracy.' },
    { icon: 'payments', title: 'Secure Escrow', description: 'Funds are held safely until the livestock is received and inspected at the destination.' },
    { icon: 'local_shipping', title: 'Climate-Safe Logistics', description: 'Partnership with livestock transit specialists for stress-free animal movement.' },
  ];

  const timeLeft = (endTime) => {
    const diff = new Date(endTime) - new Date();
    if (diff <= 0) return 'Ended';
    const h = Math.floor(diff / 3600000);
    const m = Math.floor((diff % 3600000) / 60000);
    return `${h}h ${m}m`;
  };

  return (
    <div className="bg-background text-on-surface font-body">
      <Navbar variant="default" />

      <main className="pt-20">

        {/* ── HERO ── */}
        <section className="relative h-[870px] flex items-center justify-center overflow-hidden">
          <div className="absolute inset-0 z-0">
            <img
              className="w-full h-full object-cover brightness-75"
              style={{ objectPosition: 'center 30%' }}
              src="https://lh3.googleusercontent.com/aida-public/AB6AXuDfsl01tTn-hVDG7p-WvDCVX3XdN_BGl1NvnPvkgV5vwh0JWPoCa7GXgE6dDRnDTrNey4uw-t6iHSW5ufw7qjCw5_IMW_E6PQGHubjCpmuGL9m4uC87jkpdIE00XlDaJpJKYraCCrQJ0_LaqbKVg1qck_8PrllLS3TV3L_Aw3QkftgJIHIbK7JMD4oqLHC2JyisWL22tulMprWo3pEZ0kQBgKYMZWbk8RlMlXXjKJ7zBoEb7Iks8DnFMFjzWeYHrONEZ7a_JXv8NsCj"
              alt="Livestock marketplace hero"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
          </div>

          {/* Floating stat pills */}
          <div className="absolute top-32 left-8 hidden lg:flex flex-col gap-3 z-10">
            <div className="bg-white/15 backdrop-blur-xl border border-white/20 rounded-2xl px-5 py-3 text-white">
              <p className="text-2xl font-black">{recentListings.length}+</p>
              <p className="text-xs text-white/70 font-medium">Live Listings</p>
            </div>
            <div className="bg-white/15 backdrop-blur-xl border border-white/20 rounded-2xl px-5 py-3 text-white">
              <p className="text-2xl font-black">{breeders.length}+</p>
              <p className="text-xs text-white/70 font-medium">Verified Breeders</p>
            </div>
          </div>

          {auctions.length > 0 && (
            <div className="absolute top-32 right-8 hidden lg:block z-10">
              <div className="bg-red-500/90 backdrop-blur-xl border border-red-400/30 rounded-2xl px-5 py-3 text-white cursor-pointer hover:bg-red-600/90 transition-all"
                onClick={() => navigate('/auctions')}>
                <div className="flex items-center gap-2 mb-1">
                  <div className="w-2 h-2 rounded-full bg-white animate-pulse" />
                  <p className="text-xs font-bold uppercase tracking-widest">Live Auction</p>
                </div>
                <p className="font-black text-sm">{auctions[0].animal_name}</p>
                <p className="text-xs text-white/80">Ends in {timeLeft(auctions[0].end_time)}</p>
              </div>
            </div>
          )}

          <div className="relative z-10 w-full max-w-4xl px-6 text-center space-y-8">
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur border border-white/20 rounded-full px-4 py-2 text-white/80 text-sm font-medium mb-4">
              <span className="w-2 h-2 rounded-full bg-primary animate-pulse" />
              India's Premier Livestock Marketplace
            </div>
            <h1 className="font-headline text-5xl md:text-7xl font-extrabold text-white tracking-tight leading-tight">
              The Modern <br />
              <span className="text-primary-fixed">Pastoral Market</span>
            </h1>
            <p className="text-white/85 text-lg md:text-xl max-w-2xl mx-auto font-light leading-relaxed">
              Discover elite livestock from the country's most trusted breeders. Verified lineage, secure payments, doorstep delivery.
            </p>
            <div className="mt-12 bg-white/10 backdrop-blur-xl p-2 rounded-full border border-white/20 max-w-2xl mx-auto flex items-center shadow-2xl">
              <div className="flex-1 flex items-center px-6">
                <span className="material-symbols-outlined text-white/60 mr-3">search</span>
                <input
                  className="bg-transparent border-none focus:ring-0 text-white placeholder:text-white/60 w-full py-3 text-lg outline-none"
                  placeholder="Search breed, category, location..."
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && navigate(`/search?search=${encodeURIComponent(searchQuery)}`)}
                />
              </div>
              <button
                className="bg-primary text-white px-8 py-3 rounded-full font-bold hover:bg-primary/90 transition-all active:scale-95"
                onClick={() => navigate(`/search?search=${encodeURIComponent(searchQuery)}`)}>
                Explore
              </button>
            </div>
            {/* Quick links */}
            <div className="flex items-center justify-center gap-3 flex-wrap pt-2">
              {['Goat', 'Sheep', 'Bakra'].map(cat => (
                <button key={cat}
                  onClick={() => navigate(`/search?category=${cat}`)}
                  className="bg-white/10 hover:bg-white/20 border border-white/20 text-white text-sm font-semibold px-4 py-1.5 rounded-full transition-all backdrop-blur">
                  {cat}
                </button>
              ))}
              <button onClick={() => navigate('/search?mode=meat')}
                className="bg-red-500/80 hover:bg-red-500 border border-red-400/30 text-white text-sm font-semibold px-4 py-1.5 rounded-full transition-all backdrop-blur flex items-center gap-1">
                🥩 Meat Products
              </button> 
            </div>
          </div>
        </section>

        {/* ── STATS BAR ── */}
        <section className="bg-primary text-white py-6">
          <div className="max-w-screen-2xl mx-auto px-6 grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            {[
              { label: 'Active Listings', value: recentListings.filter(a => a.status === 'active').length || recentListings.length },
              { label: 'Verified Breeders', value: breeders.length },
              { label: 'Meat Products', value: meatProducts.length },
              { label: 'Live Auctions', value: auctions.length },
            ].map((stat, i) => (
              <div key={i}>
                <p className="text-3xl font-black text-primary-fixed">{stat.value}+</p>
                <p className="text-white/70 text-sm font-medium">{stat.label}</p>
              </div>
            ))}
          </div>
        </section>

        {/* ── FEATURED BENTO ── */}
        <section className="py-24 px-6 max-w-screen-2xl mx-auto">
          <div className="flex justify-between items-end mb-16">
            <div className="space-y-2">
              <p className="text-primary font-bold text-sm uppercase tracking-widest">Hand Picked</p>
              <h2 className="font-headline text-4xl font-extrabold tracking-tighter text-on-surface">Featured Stock</h2>
              <div className="h-1 w-20 bg-primary rounded-full" />
            </div>
            <button className="text-primary font-bold flex items-center gap-2 hover:gap-3 transition-all group" onClick={() => navigate('/search')}>
              View All <span className="material-symbols-outlined transition-transform group-hover:translate-x-1">arrow_forward</span>
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-6 min-h-[500px]">
            {featuredListings[0] && (
              <div className="md:col-span-7 group relative overflow-hidden rounded-3xl bg-surface-container-lowest cursor-pointer shadow-xl"
                onClick={() => navigate(`/product/${featuredListings[0].public_id || featuredListings[0].id}`)}>
                <img
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105 min-h-[400px]"
                  style={{ objectPosition: 'center 10%' }}
                  src={getImageUrl(featuredListings[0].image_url)}
                  alt={featuredListings[0].name}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/20 to-transparent" />
                <div className="absolute top-4 left-4 flex gap-2">
                  <span className="bg-primary text-white px-3 py-1 rounded-full text-xs font-black uppercase tracking-widest">
                    {featuredListings[0].badge || 'Featured'}
                  </span>
                  <span className="bg-white/20 backdrop-blur text-white px-3 py-1 rounded-full text-xs font-semibold">
                    {featuredListings[0].category}
                  </span>
                </div>
                <div className="absolute bottom-0 left-0 p-8 w-full text-white">
                  <h3 className="font-headline text-3xl font-bold mb-2">{featuredListings[0].name}</h3>
                  <div className="flex gap-4 text-white/70 text-sm mb-4">
                    <span className="flex items-center gap-1"><span className="material-symbols-outlined text-sm">scale</span>{featuredListings[0].weight_kg}kg</span>
                    <span className="flex items-center gap-1"><span className="material-symbols-outlined text-sm">cake</span>{featuredListings[0].age_months}mo</span>
                    <span className="flex items-center gap-1"><span className="material-symbols-outlined text-sm">location_on</span>{featuredListings[0].location_city}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <p className="text-4xl font-black text-primary-fixed">₹{Number(featuredListings[0].price_inr).toLocaleString('en-IN')}</p>
                    <button className="bg-white text-emerald-900 px-6 py-2.5 rounded-full font-bold hover:bg-primary hover:text-white transition-all">
                      View Details
                    </button>
                  </div>
                </div>
              </div>
            )}

            <div className="md:col-span-5 flex flex-col gap-6">
              {featuredListings[1] && (
                <div className="flex-1 group relative overflow-hidden rounded-3xl bg-surface-container-lowest cursor-pointer shadow-lg"
                  onClick={() => navigate(`/product/${featuredListings[1].public_id || featuredListings[1].id}`)}>
                  <img className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 min-h-[180px]"
                    style={{ objectPosition: 'center 10%' }}
                    src={getImageUrl(featuredListings[1].image_url)} alt={featuredListings[1].name} />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
                  <div className="absolute bottom-0 p-6 w-full text-white">
                    <div className="flex justify-between items-end">
                      <div>
                        <p className="text-xs text-white/60 font-semibold uppercase">{featuredListings[1].category} · {featuredListings[1].location_city}</p>
                        <h3 className="font-headline text-xl font-bold">{featuredListings[1].name}</h3>
                      </div>
                      <p className="text-2xl font-black text-primary-fixed">₹{Number(featuredListings[1].price_inr).toLocaleString('en-IN')}</p>
                    </div>
                  </div>
                </div>
              )}
              {featuredListings[2] && (
                <div className="flex-1 group relative overflow-hidden rounded-3xl bg-surface-container-lowest cursor-pointer shadow-lg"
                  onClick={() => navigate(`/product/${featuredListings[2].public_id || featuredListings[2].id}`)}>
                  <img className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 min-h-[180px]"
                    style={{ objectPosition: 'center 5%' }}
                    src={getImageUrl(featuredListings[2].image_url)} alt={featuredListings[2].name} />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
                  <div className="absolute bottom-0 p-6 w-full text-white">
                    <div className="flex justify-between items-end">
                      <div>
                        <p className="text-xs text-white/60 font-semibold uppercase">{featuredListings[2].category} · {featuredListings[2].location_city}</p>
                        <h3 className="font-headline text-xl font-bold">{featuredListings[2].name}</h3>
                      </div>
                      <p className="text-2xl font-black text-primary-fixed">₹{Number(featuredListings[2].price_inr).toLocaleString('en-IN')}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </section>

        {/* ── BROWSE BY CATEGORY ── */}
        {categories.length > 0 && (
          <section className="bg-surface-container-low py-20">
            <div className="max-w-screen-2xl mx-auto px-6">
              <div className="text-center mb-12 space-y-3">
                <p className="text-primary font-bold text-sm uppercase tracking-widest">Explore</p>
                <h2 className="font-headline text-4xl font-extrabold text-on-surface">Browse by Category</h2>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                {categories.map((cat, i) => (
                  <div key={i}
                    onClick={() => navigate(`/search?category=${encodeURIComponent(cat.name)}`)}
                    className="bg-surface-container-lowest p-6 rounded-2xl flex flex-col items-center text-center group cursor-pointer hover:bg-primary transition-all duration-300 transform hover:-translate-y-1 shadow-sm hover:shadow-xl">
                    <div className="w-14 h-14 bg-primary/10 rounded-full flex items-center justify-center mb-4 group-hover:bg-white/20 transition-colors">
                      <span className="material-symbols-outlined text-primary text-2xl group-hover:text-white">{cat.icon}</span>
                    </div>
                    <h4 className="font-bold text-sm text-on-surface group-hover:text-white">{cat.name}</h4>
                    <p className="text-xs text-on-surface-variant group-hover:text-white/70 mt-1 font-semibold">{cat.listings} listings</p>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* ── LIVE AUCTIONS ── */}
        {auctions.length > 0 && (
          <section className="py-20 px-6 max-w-screen-2xl mx-auto">
            <div className="flex justify-between items-end mb-12">
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <div className="w-2.5 h-2.5 rounded-full bg-red-500 animate-pulse" />
                  <p className="text-red-500 font-bold text-sm uppercase tracking-widest">Bidding Open</p>
                </div>
                <h2 className="font-headline text-4xl font-extrabold">Live Auctions</h2>
              </div>
              <button onClick={() => navigate('/auctions')}
                className="text-primary font-bold flex items-center gap-2 hover:gap-3 transition-all group">
                All Auctions <span className="material-symbols-outlined transition-transform group-hover:translate-x-1">arrow_forward</span>
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {auctions.map(auction => (
                <div key={auction.id}
                  onClick={() => navigate(`/auctions/${auction.public_id || auction.id}`)}
                  className="group cursor-pointer bg-surface-container-lowest rounded-3xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-1 border border-red-100">
                  <div className="relative h-52 overflow-hidden">
                    <img src={getImageUrl(auction.image_url)} alt={auction.animal_name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
                    <div className="absolute top-3 left-3 bg-red-500 text-white text-xs font-black px-3 py-1 rounded-full flex items-center gap-1">
                      <div className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" /> LIVE
                    </div>
                    <div className="absolute top-3 right-3 bg-black/60 backdrop-blur text-white text-xs font-bold px-3 py-1 rounded-full">
                      ⏱ {timeLeft(auction.end_time)}
                    </div>
                    <div className="absolute bottom-3 left-3 text-white">
                      <p className="font-black text-lg">{auction.animal_name}</p>
                      <p className="text-white/70 text-xs">{auction.breed} · {auction.city}</p>
                    </div>
                  </div>
                  <div className="p-5">
                    <div className="flex justify-between items-center mb-3">
                      <div>
                        <p className="text-xs text-on-surface-variant font-semibold">Current Bid</p>
                        <p className="text-2xl font-black text-primary">
                          ₹{Number(auction.current_bid || auction.start_price).toLocaleString('en-IN')}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-on-surface-variant font-semibold">Total Bids</p>
                        <p className="text-2xl font-black text-on-surface">{auction.total_bids}</p>
                      </div>
                    </div>
                    <button className="w-full py-2.5 rounded-xl bg-red-500 text-white font-bold text-sm hover:bg-red-600 transition-all">
                      Place Bid →
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* ── RECENTLY ADDED ── */}
        <section className="py-20 px-6 max-w-screen-2xl mx-auto">
          <div className="flex justify-between items-center mb-12">
            <div className="space-y-2">
              <p className="text-primary font-bold text-sm uppercase tracking-widest">Just Listed</p>
              <h2 className="font-headline text-3xl font-extrabold">Recently Added</h2>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-sm text-on-surface-variant font-medium">
                {currentPage * itemsPerPage + 1}–{Math.min((currentPage + 1) * itemsPerPage, recentListings.length)} of {recentListings.length}
              </span>
              <div className="flex gap-2">
                <button
                  onClick={() => setCurrentPage(p => Math.max(0, p - 1))}
                  disabled={currentPage === 0}
                  className="w-10 h-10 rounded-full border border-outline-variant flex items-center justify-center hover:bg-primary hover:text-white hover:border-primary transition-all disabled:opacity-30 disabled:cursor-not-allowed">
                  <span className="material-symbols-outlined">chevron_left</span>
                </button>
                <button
                  onClick={() => setCurrentPage(p => p + 1)}
                  disabled={(currentPage + 1) * itemsPerPage >= recentListings.length}
                  className="w-10 h-10 rounded-full border border-outline-variant flex items-center justify-center hover:bg-primary hover:text-white hover:border-primary transition-all disabled:opacity-30 disabled:cursor-not-allowed">
                  <span className="material-symbols-outlined">chevron_right</span>
                </button>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {recentListings.slice(currentPage * itemsPerPage, (currentPage + 1) * itemsPerPage).map((listing) => (
              <div key={listing.id}
                className="bg-surface-container-lowest rounded-2xl overflow-hidden group shadow-sm hover:shadow-xl transition-all duration-300 cursor-pointer hover:-translate-y-1"
                onClick={() => navigate(`/product/${listing.public_id || listing.id}`)}>
                <div className="relative h-56 overflow-hidden">
                  <img
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    style={{ objectPosition: 'center 15%' }}
                    src={getImageUrl(listing.image_url)}
                    alt={listing.name}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                  {listing.badge && (
                    <span className="absolute top-3 right-3 bg-white/90 backdrop-blur px-2.5 py-1 rounded-full text-xs font-bold text-primary">
                      {listing.badge}
                    </span>
                  )}
                  <span className="absolute top-3 left-3 bg-black/50 backdrop-blur text-white text-[10px] font-bold px-2 py-1 rounded-full uppercase">
                    {listing.category}
                  </span>
                </div>
                <div className="p-5 space-y-3">
                  <div className="flex justify-between items-start gap-2">
                    <h4 className="font-bold text-on-surface leading-tight group-hover:text-primary transition-colors">{listing.name}</h4>
                    <span className="text-primary font-black text-lg whitespace-nowrap">₹{Number(listing.price_inr).toLocaleString('en-IN')}</span>
                  </div>
                  <div className="flex justify-between text-xs text-on-surface-variant">
                    <span className="flex items-center gap-1"><span className="material-symbols-outlined text-sm">scale</span>{listing.weight_kg} kg</span>
                    <span className="flex items-center gap-1"><span className="material-symbols-outlined text-sm">cake</span>{listing.age_months} mo</span>
                    <span className="flex items-center gap-1"><span className="material-symbols-outlined text-sm">location_on</span>{listing.location_city}</span>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-on-surface-variant">
                    <span className="material-symbols-outlined text-sm text-primary">storefront</span>
                    <span className="truncate">{listing.seller_name}</span>
                  </div>
                  <button className="w-full py-2 rounded-xl border-2 border-primary text-primary font-bold text-sm hover:bg-primary hover:text-white transition-all">
                    View Details
                  </button>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* ── MEAT PRODUCTS ── */}
        {meatProducts.length > 0 && (
          <section className="py-20 bg-surface-container-low">
            <div className="max-w-screen-2xl mx-auto px-6">
              <div className="flex justify-between items-end mb-12">
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <span className="text-xl">🥩</span>
                    <p className="text-red-600 font-bold text-sm uppercase tracking-widest">Fresh & Verified</p>
                  </div>
                  <h2 className="font-headline text-4xl font-extrabold">Meat Products</h2>
                  <div className="h-1 w-20 bg-red-400 rounded-full" />
                </div>
                <button onClick={() => navigate('/search?mode=meat')}
                  className="text-red-600 font-bold flex items-center gap-2 hover:gap-3 transition-all group">
                  View All <span className="material-symbols-outlined transition-transform group-hover:translate-x-1">arrow_forward</span>
                </button>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {meatProducts.map(item => (
                  <div key={item.id}
                    onClick={() => navigate(`/meat-product/${item.public_id || item.id}`)}
                    className="group cursor-pointer bg-surface-container-lowest rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                    <div className="relative h-48 overflow-hidden">
                      <img src={getImageUrl(item.image_url)} alt={item.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                      <div className="absolute top-3 left-3 bg-red-600 text-white text-[10px] font-black px-2.5 py-1 rounded-full uppercase tracking-wider">
                        {item.category}
                      </div>
                      {item.stock_qty <= 0 && (
                        <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                          <span className="bg-orange-500 text-white text-xs font-bold px-3 py-1 rounded-full">Out of Stock</span>
                        </div>
                      )}
                    </div>
                    <div className="p-5 space-y-3">
                      <div className="flex justify-between items-start gap-2">
                        <h3 className="font-bold text-on-surface leading-tight group-hover:text-red-600 transition-colors">{item.name}</h3>
                        <span className="text-red-600 font-black text-lg whitespace-nowrap">₹{Number(item.price_inr).toLocaleString('en-IN')}</span>
                      </div>
                      <div className="flex justify-between text-xs text-on-surface-variant">
                        {item.weight_kg && <span className="flex items-center gap-1"><span className="material-symbols-outlined text-sm">scale</span>{item.weight_kg} {item.unit || 'kg'}</span>}
                        <span className="flex items-center gap-1"><span className="material-symbols-outlined text-sm">location_on</span>{item.location_city || item.seller_city || '—'}</span>
                      </div>
                      <div className="flex items-center gap-1 text-xs text-on-surface-variant">
                        <span className="material-symbols-outlined text-sm text-red-500">storefront</span>
                        <span className="truncate">{item.seller_name}</span>
                      </div>
                      <button className="w-full py-2 rounded-xl bg-red-50 text-red-700 font-bold text-sm hover:bg-red-600 hover:text-white transition-all border border-red-200 hover:border-red-600">
                        View Product
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* ── TOP BREEDERS ── */}
        {breeders.length > 0 && (
          <section className="py-20 px-6 max-w-screen-2xl mx-auto">
            <div className="flex justify-between items-end mb-12">
              <div className="space-y-2">
                <p className="text-primary font-bold text-sm uppercase tracking-widest">Trusted Sellers</p>
                <h2 className="font-headline text-4xl font-extrabold">Top Breeders</h2>
              </div>
              <button onClick={() => navigate('/breeders')}
                className="text-primary font-bold flex items-center gap-2 hover:gap-3 transition-all group">
                All Breeders <span className="material-symbols-outlined transition-transform group-hover:translate-x-1">arrow_forward</span>
              </button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {breeders.map(breeder => (
                <div key={breeder.id}
                  onClick={() => navigate(`/breeder/${breeder.public_id || breeder.id}`)}
                  className="group cursor-pointer bg-surface-container-lowest rounded-2xl p-6 shadow-sm hover:shadow-xl transition-all duration-300 hover:-translate-y-1 border border-outline-variant/10 hover:border-primary/20">
                  <div className="flex items-center gap-4 mb-5">
                    <img
                      src={breeder.profile_image
                        ? getImageUrl(breeder.profile_image)
                        : `https://ui-avatars.com/api/?name=${encodeURIComponent(breeder.name)}&background=06A788&color=fff&size=128`}
                      alt={breeder.name}
                      className="w-14 h-14 rounded-full object-cover border-2 border-primary/20 ring-2 ring-white"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1">
                        <h3 className="font-bold text-on-surface truncate group-hover:text-primary transition-colors">{breeder.name}</h3>
                        <span className="material-symbols-outlined text-primary text-base flex-shrink-0">verified</span>
                      </div>
                      <p className="text-xs text-on-surface-variant flex items-center gap-1 mt-0.5">
                        <span className="material-symbols-outlined text-xs">location_on</span>
                        {breeder.city}, {breeder.state}
                      </p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="bg-surface-container-low rounded-xl p-3 text-center">
                      <p className="text-xl font-black text-primary">{breeder.live_inventory}</p>
                      <p className="text-[10px] text-on-surface-variant font-bold uppercase">Listings</p>
                    </div>
                    <div className="bg-surface-container-low rounded-xl p-3 text-center">
                      <p className="text-xl font-black text-on-surface">{breeder.total_sales}</p>
                      <p className="text-[10px] text-on-surface-variant font-bold uppercase">Sales</p>
                    </div>
                  </div>
                  {breeder.businessName && (
                    <p className="mt-3 text-xs text-on-surface-variant text-center truncate font-medium">{breeder.businessName}</p>
                  )}
                </div>
              ))}
            </div>
          </section>
        )}

        {/* ── HOW IT WORKS ── */}
        <section className="py-24 bg-primary-container text-on-primary-container overflow-hidden relative">
          <div className="absolute right-0 top-0 w-1/3 h-full bg-primary/20 -skew-x-12 translate-x-1/2" />
          <div className="max-w-screen-2xl mx-auto px-6 relative z-10">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-20 items-center">
              <div className="space-y-8">
                <div>
                  <p className="text-primary font-bold text-sm uppercase tracking-widest mb-3">Why Choose Us</p>
                  <h2 className="font-headline text-5xl font-extrabold leading-tight">
                    Elevating the Trade <br />Since 2024.
                  </h2>
                </div>
                <p className="text-on-primary-container/80 text-lg">
                  More than a marketplace — a heritage platform connecting conscientious buyers with the finest genetic legacies in pastoral farming.
                </p>
                <div className="space-y-5">
                  {features.map((feature, i) => (
                    <div key={i} className="flex gap-5 items-start">
                      <div className="bg-white/20 p-3 rounded-xl backdrop-blur flex-shrink-0">
                        <span className="material-symbols-outlined">{feature.icon}</span>
                      </div>
                      <div>
                        <h4 className="font-bold text-xl">{feature.title}</h4>
                        <p className="text-on-primary-container/70 text-sm">{feature.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="flex gap-4 flex-wrap">
                  <button className="bg-white text-primary px-8 py-4 rounded-full font-black uppercase tracking-widest text-sm shadow-lg hover:scale-105 transition-all"
                    onClick={() => navigate('/search')}>
                    Get Started
                  </button>
                  <button className="border-2 border-white/40 text-white px-8 py-4 rounded-full font-bold text-sm hover:bg-white/10 transition-all"
                    onClick={() => navigate('/auctions')}>
                    View Auctions
                  </button>
                </div>
              </div>
              <div className="relative">
                <img
                  className="rounded-3xl shadow-2xl w-full aspect-square object-cover"
                  src="https://lh3.googleusercontent.com/aida-public/AB6AXuDr7JDXf6mJqGBLJr-BwmkEcwo4Nz7mHbjd6EbMUlRKDBpJ_NPLMXUa1mTWM6-GGDNMnE14VxUSQCRFd1sDiyofkoQ8bokd7rMttE921wkearKT9feDUUiBHTvmoTOApBE0DD43rK8vAyVPQh22vdgoVVgXmjQZT2fSXJdwWkt32yhZKJtrKypjEjrUmKcKPxDtkVv33P-Fq6xZvstg_b_1i6EX85WJVgw_xPgL1m5iBkpF9hXBio0LJ1KbUSYncvC2kOj61HEovHjE"
                  alt="Farmer with livestock"
                />
                <div className="absolute -bottom-8 -left-8 bg-white p-6 rounded-2xl shadow-2xl max-w-[220px]">
                  <p className="text-on-surface font-headline font-extrabold text-3xl">12k+</p>
                  <p className="text-zinc-500 text-sm">Successful trades across India this year.</p>
                </div>
                <div className="absolute -top-6 -right-6 bg-primary text-white p-4 rounded-2xl shadow-xl">
                  <p className="font-black text-2xl">100%</p>
                  <p className="text-white/80 text-xs font-semibold">Verified Sellers</p>
                </div>
              </div>
            </div>
          </div>
        </section>

      </main>
      <Footer variant="default" />
    </div>
  );
};

export default HomePage;